import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const AdminDashboard = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/images`);
        setImages(response.data.images || []);
      } catch (err) {
        console.error('Error fetching images:', err);
        // Instead of showing an error, just set empty images array
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [API_URL]);

  const handleDownloadImage = async (id) => {
    try {
      // Get the authentication token from localStorage
      const token = localStorage.getItem('token');
      
      // Make an authenticated request to get the download URL
      const response = await axios.get(`${API_URL}/images/${id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Open the download URL in a new tab
      if (response.data && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        alert('Download link not available');
      }
    } catch (err) {
      console.error('Error downloading image:', err);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleDeleteImage = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await axios.delete(`${API_URL}/images/${id}`);
        setImages(images.filter(image => image.id !== id));
      } catch (err) {
        console.error('Error deleting image:', err);
        alert('Failed to delete image. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <div className="loading-spinner"></div>
        <p>Loading images...</p>
      </LoadingContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <h1>Admin Dashboard</h1>
      </DashboardHeader>

      <StatsContainer>
        <StatCard>
          <StatTitle>Total Images</StatTitle>
          <StatValue>{images.length}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Storage</StatTitle>
          <StatValue>
            {(images.reduce((total, img) => total + (img.size || 0), 0) / (1024 * 1024 * 1024)).toFixed(2)} GB
          </StatValue>
        </StatCard>
      </StatsContainer>

      <SectionTitle>Recent Images</SectionTitle>
      
      {images.length === 0 ? (
        <EmptyState>
          <p>No images found.</p>
        </EmptyState>
      ) : (
        <ImagesTable>
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {images.map(image => (
              <tr key={image.id}>
                <td>{image.name}</td>
                <td>{image.version}</td>
                <td>{(image.size / (1024 * 1024 * 1024)).toFixed(2)} GB</td>
                <td>{new Date(image.uploadedAt).toLocaleDateString()}</td>
                <td>
                  <ActionButtons>
                    <ActionButton 
                      onClick={() => handleDownloadImage(image.id)}
                      className="download"
                    >
                      Download
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleDeleteImage(image.id)}
                      className="delete"
                    >
                      Delete
                    </ActionButton>
                  </ActionButtons>
                </td>
              </tr>
            ))}
          </tbody>
        </ImagesTable>
      )}
    </DashboardContainer>
  );
};

// Styled components
const DashboardContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;



const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.small};
`;

const StatTitle = styled.h3`
  color: ${props => props.theme.colors.lightText};
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes.xxlarge};
  font-weight: bold;
  color: ${props => props.theme.colors.opplaneBlue};
`;

const SectionTitle = styled.h2`
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  padding-bottom: ${props => props.theme.spacing.sm};
`;

const ImagesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};
  overflow: hidden;

  th, td {
    padding: ${props => props.theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  th {
    background-color: ${props => props.theme.colors.background};
    font-weight: bold;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover {
    background-color: ${props => props.theme.colors.background};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.small};
  transition: ${props => props.theme.transitions.short};

  &.download {
    background-color: ${props => props.theme.colors.opplaneBlue};
    color: white;
    
    &:hover {
      background-color: ${props => props.theme.colors.opplaneDarkBlue};
    }
  }

  &.delete {
    background-color: ${props => props.theme.colors.error};
    color: white;
    
    &:hover {
      background-color: #b02a37;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};

  p {
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.lightText};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};

  .loading-spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid ${props => props.theme.colors.opplaneBlue};
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: ${props => props.theme.spacing.md};
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default AdminDashboard;
